import { ARDUINO_UNO_PIN_ORDER } from './avr8Simulator';
import {
  resolvePassiveComponentRuntimeSimulationModel,
  resolveVariableResistorSimulationState,
} from './projectBuilderPassives';
import {
  getBreadboardBusGroupsFromMarkerKeys,
  isBreadboardComponent,
} from './projectBuilderBreadboard';
import {
  buildSevenSegmentSegmentStates,
  getSevenSegmentCommonTypeLabel,
  getSevenSegmentDigitPinKeys,
  getSevenSegmentSegmentKeyByPin,
  getSevenSegmentSegmentPinKey,
  normalizeSevenSegmentCommonType,
  normalizeSevenSegmentDigitCount,
} from './sevenSegmentDisplay';
import { getPinAlternateNames } from './pinAlternateNames';

const ARDUINO_UNO_PIN_NAMES = new Set(
  ARDUINO_UNO_PIN_ORDER.map((pin) => pin.name)
);

const ARDUINO_UNO_PIN_NAME_BY_TOKEN = new Map(
  Array.from(ARDUINO_UNO_PIN_NAMES).map((pinName) => [
    normalizePinToken(pinName),
    pinName,
  ])
);

const ARDUINO_UNO_ANALOG_PIN_NAMES = ARDUINO_UNO_PIN_ORDER
  .map((pin) => pin.name)
  .filter((pinName) => /^A[0-5]$/.test(pinName));

const DEFAULT_ANALOG_REFERENCE_VOLTAGE = 5;
const ADC_MAX_VALUE = 1023;
const VOLTAGE_EPSILON = 1e-6;
const MIN_SIMULATED_RESISTANCE_OHMS = 1e-3;

const buildEndpointId = (partId, pinKey) => `${partId}:${pinKey}`;

function stripMarkerIndex(value) {
  return String(value || '').replace(/__\d+$/, '');
}

function parseEndpointId(endpointId) {
  const safeEndpointId = String(endpointId || '');
  const separatorIndex = safeEndpointId.indexOf(':');

  if (separatorIndex < 0) {
    return {
      partId: '',
      pinKey: safeEndpointId,
    };
  }

  return {
    partId: safeEndpointId.slice(0, separatorIndex),
    pinKey: safeEndpointId.slice(separatorIndex + 1),
  };
}

function normalizePinToken(value) {
  return String(value || '')
    .trim()
    .toUpperCase()
    .replace(/[\s_-]+/g, '');
}

function uniqueValues(values) {
  return Array.from(
    new Set(
      values
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    )
  );
}

function ensureAdjacencySet(adjacency, endpointId) {
  if (!adjacency.has(endpointId)) {
    adjacency.set(endpointId, new Set());
  }

  return adjacency.get(endpointId);
}

function addUndirectedEdge(adjacency, fromId, toId) {
  if (!fromId || !toId || fromId === toId) {
    return;
  }

  ensureAdjacencySet(adjacency, fromId).add(toId);
  ensureAdjacencySet(adjacency, toId).add(fromId);
}

function addBreadboardBusEdges(adjacency, component, suggestionPins) {
  if (!isBreadboardComponent(component)) {
    return;
  }

  const componentId = String(component?.id || '').trim();

  if (!componentId) {
    return;
  }

  const markerKeys = (Array.isArray(suggestionPins) ? suggestionPins : [])
    .filter((pin) => String(pin?.component_id || '') === componentId)
    .map((pin) => String(pin?.marker_key || pin?.markerKey || '').trim())
    .filter(Boolean);

  getBreadboardBusGroupsFromMarkerKeys(markerKeys).forEach((group) => {
    const [firstMarkerKey, ...restMarkerKeys] = group;

    restMarkerKeys.forEach((markerKey) => {
      addUndirectedEdge(
        adjacency,
        buildEndpointId(componentId, firstMarkerKey),
        buildEndpointId(componentId, markerKey)
      );
    });
  });
}

function normalizePressedButtonIds(pressedButtonIds) {
  if (pressedButtonIds instanceof Set) {
    return pressedButtonIds;
  }

  if (Array.isArray(pressedButtonIds)) {
    return new Set(pressedButtonIds);
  }

  return new Set();
}

function isArduinoUnoBoard(board) {
  const slug = normalizePinToken(board?.slug);
  const name = normalizePinToken(board?.name);
  const manufacturer = normalizePinToken(board?.manufacturer);

  return (
    slug.includes('ARDUINOUNO') ||
    name.includes('ARDUINOUNO') ||
    (manufacturer === 'ARDUINO' && name.includes('UNO'))
  );
}

function getPinCandidateTokens(pin) {
  const alternateNames = getPinAlternateNames(pin);

  return uniqueValues([
    pin?.pin_name,
    pin?.pinName,
    pin?.pin_number,
    pin?.pinNumber,
    pin?.display_name,
    pin?.displayName,
    pin?.marker_key,
    pin?.markerKey,
    pin?.gpio_name,
    pin?.gpioName,
    ...alternateNames,
  ]).map(normalizePinToken);
}

function isGroundPin(pin) {
  const pinRole = normalizePinToken(pin?.pin_role || pin?.pinRole);

  if (pinRole === 'GROUND') {
    return true;
  }

  return getPinCandidateTokens(pin).some((token) => (
    token === 'GROUND' || token.startsWith('GND')
  ));
}

function isFiveVoltPin(pin) {
  return getPinCandidateTokens(pin).some((token) => (
    token === '5V' || token === 'VCC' || token === '+5V' || token.endsWith('5V')
  ));
}

function resolveArduinoUnoPinNameFromToken(token) {
  const safeToken = normalizePinToken(token);

  if (ARDUINO_UNO_PIN_NAME_BY_TOKEN.has(safeToken)) {
    return ARDUINO_UNO_PIN_NAME_BY_TOKEN.get(safeToken);
  }

  if (safeToken === 'RX') {
    return 'D0';
  }

  if (safeToken === 'TX') {
    return 'D1';
  }

  const digitalPrefixMatch = safeToken.match(/^D(\d{1,2})(?:\D.*)?$/);

  if (digitalPrefixMatch) {
    const pinNumber = Number(digitalPrefixMatch[1]);

    if (pinNumber >= 0 && pinNumber <= 13) {
      return `D${pinNumber}`;
    }
  }

  const analogPrefixMatch = safeToken.match(/^A([0-5])(?:\D.*)?$/);

  if (analogPrefixMatch) {
    return `A${analogPrefixMatch[1]}`;
  }

  const pinMatch = safeToken.match(/^(?:PIN)?(\d{1,2})$/);

  if (pinMatch) {
    const pinNumber = Number(pinMatch[1]);

    if (pinNumber >= 0 && pinNumber <= 13) {
      return `D${pinNumber}`;
    }

    if (pinNumber >= 14 && pinNumber <= 19) {
      return `A${pinNumber - 14}`;
    }
  }

  return '';
}

function resolveArduinoUnoPinName(pin) {
  if (isGroundPin(pin) || isFiveVoltPin(pin)) {
    return '';
  }

  const resolvedPinName = getPinCandidateTokens(pin)
    .map(resolveArduinoUnoPinNameFromToken)
    .find(Boolean);

  return resolvedPinName || '';
}

function buildEndpointPinResolver(suggestionPins) {
  const exactEndpointPins = new Map();
  const pinsByComponentId = new Map();

  (Array.isArray(suggestionPins) ? suggestionPins : []).forEach((pin) => {
    const componentId = String(pin?.component_id || '');

    if (!componentId) {
      return;
    }

    if (!pinsByComponentId.has(componentId)) {
      pinsByComponentId.set(componentId, []);
    }

    pinsByComponentId.get(componentId).push(pin);

    if (pin?.marker_key) {
      exactEndpointPins.set(
        buildEndpointId(componentId, pin.marker_key),
        pin
      );
    }
  });

  return {
    exactEndpointPins,
    pinsByComponentId,
  };
}

function resolveEndpointPin(componentId, pinKey, endpointPinResolver) {
  if (!componentId || !pinKey) {
    return null;
  }

  const exactEndpointPin = endpointPinResolver?.exactEndpointPins?.get(
    buildEndpointId(componentId, pinKey)
  );

  if (exactEndpointPin) {
    return exactEndpointPin;
  }

  const componentPins =
    endpointPinResolver?.pinsByComponentId?.get(String(componentId)) || [];

  if (componentPins.length === 0) {
    return null;
  }

  const targetToken = normalizePinToken(stripMarkerIndex(pinKey));

  if (!targetToken) {
    return null;
  }

  const markerBaseMatch = componentPins.find((pin) => (
    normalizePinToken(stripMarkerIndex(pin?.marker_key || pin?.markerKey)) === targetToken
  ));

  if (markerBaseMatch) {
    return markerBaseMatch;
  }

  const exactCandidateMatch = componentPins.find((pin) => (
    getPinCandidateTokens(pin).includes(targetToken)
  ));

  if (exactCandidateMatch) {
    return exactCandidateMatch;
  }

  return componentPins.find((pin) => (
    getPinCandidateTokens(pin).some((candidateToken) => (
      candidateToken &&
      (candidateToken.includes(targetToken) || targetToken.includes(candidateToken))
    ))
  )) || null;
}

function resolveEndpointPinFromId(endpointId, endpointPinResolver) {
  const { partId, pinKey } = parseEndpointId(endpointId);

  return resolveEndpointPin(partId, pinKey, endpointPinResolver);
}

function buildBaseCircuitAdjacency(connections, suggestionPins) {
  const adjacency = new Map();

  (Array.isArray(connections) ? connections : []).forEach((connection) => {
    const fromId = buildEndpointId(connection?.from?.partId, connection?.from?.pinKey);
    const toId = buildEndpointId(connection?.to?.partId, connection?.to?.pinKey);
    addUndirectedEdge(adjacency, fromId, toId);
  });

  (Array.isArray(suggestionPins) ? suggestionPins : []).forEach((pin) => {
    const componentId = String(pin?.component_id || '').trim();
    const markerKey = String(pin?.marker_key || pin?.markerKey || '').trim();

    if (!componentId || !markerKey) {
      return;
    }

    const markerEndpointId = buildEndpointId(componentId, markerKey);

    getPinCandidateTokens(pin).forEach((token) => {
      const aliasKey = String(token || '').trim();

      if (!aliasKey || aliasKey === markerKey) {
        return;
      }

      addUndirectedEdge(
        adjacency,
        markerEndpointId,
        buildEndpointId(componentId, aliasKey)
      );
    });

    const markerBase = stripMarkerIndex(markerKey);

    if (markerBase && markerBase !== markerKey) {
      addUndirectedEdge(
        adjacency,
        markerEndpointId,
        buildEndpointId(componentId, markerBase)
      );
    }
  });

  return adjacency;
}

function buildCircuitAdjacency(components, connections, suggestionPins, options = {}) {
  const adjacency = buildBaseCircuitAdjacency(connections, suggestionPins);
  const simulationRuntimeState = {
    pressedButtonIds: normalizePressedButtonIds(options.pressedButtonIds),
  };

  (Array.isArray(components) ? components : []).forEach((component) => {
    addBreadboardBusEdges(adjacency, component, suggestionPins);
    addPassiveModelEdges(adjacency, component, simulationRuntimeState);
  });

  return adjacency;
}

function buildHardCircuitAdjacency(components, connections, suggestionPins, options = {}) {
  const adjacency = buildBaseCircuitAdjacency(connections, suggestionPins);
  const simulationRuntimeState = {
    pressedButtonIds: normalizePressedButtonIds(options.pressedButtonIds),
  };

  (Array.isArray(components) ? components : []).forEach((component) => {
    addBreadboardBusEdges(adjacency, component, suggestionPins);
    addPassiveModelEdges(
      adjacency,
      component,
      simulationRuntimeState,
      shouldTreatPassiveEdgesAsHardConnectivity
    );
  });

  return adjacency;
}

function collectReachableEndpoints(startId, adjacency) {
  if (!startId) {
    return new Set();
  }

  const visited = new Set([startId]);
  const queue = [startId];

  while (queue.length > 0) {
    const endpointId = queue.shift();
    const neighbors = adjacency.get(endpointId);

    if (!neighbors) {
      continue;
    }

    neighbors.forEach((neighborId) => {
      if (visited.has(neighborId)) {
        return;
      }

      visited.add(neighborId);
      queue.push(neighborId);
    });
  }

  return visited;
}

function collectReachableEndpointsFromStarts(startIds, adjacency) {
  const visited = new Set();

  (Array.isArray(startIds) ? startIds : []).forEach((startId) => {
    collectReachableEndpoints(startId, adjacency).forEach((endpointId) => {
      visited.add(endpointId);
    });
  });

  return visited;
}

function resolveEndpointPotential(endpointPin, unoComponentIds, simulationPinStates) {
  if (
    !endpointPin ||
    endpointPin.component_type !== 'board' ||
    !unoComponentIds.has(endpointPin.component_id)
  ) {
    return null;
  }

  if (isGroundPin(endpointPin)) {
    return {
      level: 'low',
      label: 'GND',
      voltage: 0,
    };
  }

  if (isFiveVoltPin(endpointPin)) {
    return {
      level: 'high',
      label: '5V',
      voltage: DEFAULT_ANALOG_REFERENCE_VOLTAGE,
    };
  }

  const drivenVoltage = resolveEndpointDrivenVoltage(
    endpointPin,
    unoComponentIds,
    simulationPinStates
  );

  if (drivenVoltage?.level === 'high' || drivenVoltage?.level === 'low') {
    return drivenVoltage;
  }

  return null;
}

function resolveEndpointDrivenVoltage(endpointPin, unoComponentIds, simulationPinStates) {
  if (
    !endpointPin ||
    endpointPin.component_type !== 'board' ||
    !unoComponentIds.has(endpointPin.component_id)
  ) {
    return null;
  }

  if (isGroundPin(endpointPin)) {
    return {
      level: 'low',
      label: 'GND',
      voltage: 0,
    };
  }

  if (isFiveVoltPin(endpointPin)) {
    return {
      level: 'high',
      label: '5V',
      voltage: DEFAULT_ANALOG_REFERENCE_VOLTAGE,
    };
  }

  const unoPinName = resolveArduinoUnoPinName(endpointPin);

  if (!unoPinName) {
    return null;
  }

  const pinState = simulationPinStates?.[unoPinName]?.state;
  const averageVoltage = Number(simulationPinStates?.[unoPinName]?.averageVoltage);

  if (simulationPinStates?.[unoPinName]?.outputMode === 'pwm' && Number.isFinite(averageVoltage)) {
    return {
      level: 'analog',
      label: unoPinName,
      voltage: clampNumber(averageVoltage, 0, DEFAULT_ANALOG_REFERENCE_VOLTAGE),
    };
  }

  if (pinState === 'high') {
    return {
      level: 'high',
      label: unoPinName,
      voltage: DEFAULT_ANALOG_REFERENCE_VOLTAGE,
    };
  }

  if (pinState === 'low') {
    return {
      level: 'low',
      label: unoPinName,
      voltage: 0,
    };
  }

  if (simulationPinStates?.[unoPinName]?.isOutput) {
    return {
      level: simulationPinStates[unoPinName].isHigh ? 'high' : 'low',
      label: unoPinName,
      voltage: simulationPinStates[unoPinName].isHigh ? DEFAULT_ANALOG_REFERENCE_VOLTAGE : 0,
    };
  }

  return null;
}

function collectReachablePotentials(reachableEndpoints, endpointPinResolver, unoComponentIds, simulationPinStates) {
  const potentials = [];
  const seen = new Set();

  reachableEndpoints.forEach((endpointId) => {
    const endpointPin = resolveEndpointPinFromId(endpointId, endpointPinResolver);
    const resolvedPotential = resolveEndpointPotential(
      endpointPin,
      unoComponentIds,
      simulationPinStates
    );

    if (!resolvedPotential) {
      return;
    }

    const key = `${resolvedPotential.level}:${resolvedPotential.label}`;

    if (seen.has(key)) {
      return;
    }

    seen.add(key);
    potentials.push(resolvedPotential);
  });

  return potentials;
}

function collectReachableArduinoPinNames(reachableEndpoints, endpointPinResolver, unoComponentIds) {
  const pinNames = [];
  const seen = new Set();

  reachableEndpoints.forEach((endpointId) => {
    const endpointPin = resolveEndpointPinFromId(endpointId, endpointPinResolver);

    if (
      !endpointPin ||
      endpointPin.component_type !== 'board' ||
      !unoComponentIds.has(endpointPin.component_id)
    ) {
      return;
    }

    const pinName = resolveArduinoUnoPinName(endpointPin);

    if (!pinName || seen.has(pinName)) {
      return;
    }

    seen.add(pinName);
    pinNames.push(pinName);
  });

  return pinNames;
}

function collectReachableArduinoRailLabels(reachableEndpoints, endpointPinResolver, unoComponentIds) {
  const railLabels = [];
  const seen = new Set();

  reachableEndpoints.forEach((endpointId) => {
    const endpointPin = resolveEndpointPinFromId(endpointId, endpointPinResolver);

    if (
      !endpointPin ||
      endpointPin.component_type !== 'board' ||
      !unoComponentIds.has(endpointPin.component_id)
    ) {
      return;
    }

    const railLabel = isGroundPin(endpointPin)
      ? 'GND'
      : isFiveVoltPin(endpointPin)
        ? '5V'
        : '';

    if (!railLabel || seen.has(railLabel)) {
      return;
    }

    seen.add(railLabel);
    railLabels.push(railLabel);
  });

  return railLabels;
}

function getComponentDisplayName(component) {
  return (
    component?.name ||
    component?.board?.name ||
    component?.passiveDefinition?.label ||
    component?.passiveType ||
    component?.type ||
    'Component'
  );
}

function collectReachableBoardPinDetails(
  reachableEndpoints,
  endpointPinResolver,
  unoComponentIds,
  componentsById
) {
  const details = [];
  const seen = new Set();

  reachableEndpoints.forEach((endpointId) => {
    const { partId, pinKey } = parseEndpointId(endpointId);
    const endpointPin = resolveEndpointPin(partId, pinKey, endpointPinResolver);

    if (
      !endpointPin ||
      endpointPin.component_type !== 'board' ||
      !unoComponentIds.has(endpointPin.component_id)
    ) {
      return;
    }

    const component = componentsById.get(String(endpointPin.component_id));
    const railLabel = isGroundPin(endpointPin)
      ? 'GND'
      : isFiveVoltPin(endpointPin)
        ? '5V'
        : '';
    const unoPinName = resolveArduinoUnoPinName(endpointPin);
    const resolvedLabel =
      railLabel ||
      unoPinName ||
      String(
        endpointPin.display_name ||
        endpointPin.pin_name ||
        endpointPin.pin_number ||
        stripMarkerIndex(pinKey)
      ).trim();
    const canvasPinKey = String(pinKey || '').trim();
    const canvasPinBase = stripMarkerIndex(canvasPinKey);
    const dedupeKey = [
      endpointPin.component_id,
      resolvedLabel,
      canvasPinBase,
    ].join(':');

    if (seen.has(dedupeKey)) {
      return;
    }

    seen.add(dedupeKey);
    details.push({
      componentId: endpointPin.component_id,
      componentName: getComponentDisplayName(component) || endpointPin.component_name || 'Board',
      canvasPinKey,
      canvasPinBase,
      resolvedLabel,
      unoPinName,
      railLabel,
      displayName: String(
        endpointPin.display_name ||
        endpointPin.pin_name ||
        endpointPin.pin_number ||
        ''
      ).trim(),
    });
  });

  return details;
}

function hasOnlyHighPotential(potentials) {
  const hasHigh = potentials.some((potential) => potential.level === 'high');
  const hasLow = potentials.some((potential) => potential.level === 'low');
  return hasHigh && !hasLow;
}

function hasOnlyLowPotential(potentials) {
  const hasHigh = potentials.some((potential) => potential.level === 'high');
  const hasLow = potentials.some((potential) => potential.level === 'low');
  return hasLow && !hasHigh;
}

function resolveDigitalInputStateFromPotentials(potentials) {
  const hasHigh = potentials.some((potential) => potential.level === 'high');
  const hasLow = potentials.some((potential) => potential.level === 'low');

  if (hasHigh && !hasLow) {
    return {
      level: 'high',
      status: 'high',
    };
  }

  if (hasLow && !hasHigh) {
    return {
      level: 'low',
      status: 'low',
    };
  }

  if (hasHigh && hasLow) {
    return {
      level: null,
      status: 'conflict',
    };
  }

  return {
    level: null,
    status: 'floating',
  };
}

function clampNumber(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value));
}

function isApproximatelyEqual(left, right, epsilon = VOLTAGE_EPSILON) {
  return Math.abs(Number(left) - Number(right)) <= epsilon;
}

function resolveAnalogVoltageStateFromPotentials(potentials = []) {
  const voltages = [];

  (Array.isArray(potentials) ? potentials : []).forEach((potential) => {
    const voltage = Number(potential?.voltage);

    if (!Number.isFinite(voltage)) {
      return;
    }

    if (voltages.some((knownVoltage) => isApproximatelyEqual(knownVoltage, voltage))) {
      return;
    }

    voltages.push(voltage);
  });

  if (voltages.length === 0) {
    return {
      status: 'floating',
      voltage: null,
    };
  }

  if (voltages.length === 1) {
    return {
      status: 'resolved',
      voltage: voltages[0],
    };
  }

  return {
    status: 'conflict',
    voltage: null,
  };
}

function toAdcValue(voltage, referenceVoltage = DEFAULT_ANALOG_REFERENCE_VOLTAGE) {
  const safeVoltage = Number(voltage);
  const safeReferenceVoltage = Number(referenceVoltage);

  if (
    !Number.isFinite(safeVoltage) ||
    !Number.isFinite(safeReferenceVoltage) ||
    safeReferenceVoltage <= 0
  ) {
    return null;
  }

  const rawValue = (safeVoltage / safeReferenceVoltage) * 1024;
  return clampNumber(Math.floor(rawValue), 0, ADC_MAX_VALUE);
}

function getSimulationParameterNumber(parameters, key, fallbackValue) {
  const numericValue = Number(parameters?.[key]);
  return Number.isFinite(numericValue) ? numericValue : fallbackValue;
}

function normalizeServoSignalRange(parameters = {}) {
  const minimumSignalValue = getSimulationParameterNumber(parameters, 'minSignalValue', 0);
  const maximumSignalValue = getSimulationParameterNumber(parameters, 'maxSignalValue', 1);
  const lowerBound = Math.min(minimumSignalValue, maximumSignalValue);
  const upperBound = Math.max(minimumSignalValue, maximumSignalValue);
  const range = upperBound - lowerBound;

  return {
    maxSignalValue: upperBound,
    minSignalValue: lowerBound,
    range,
  };
}

function normalizeServoPulseRange(parameters = {}) {
  const minimumPulseMicros = getSimulationParameterNumber(parameters, 'minPulseMicros', 544);
  const maximumPulseMicros = getSimulationParameterNumber(parameters, 'maxPulseMicros', 2400);
  const lowerBound = Math.min(minimumPulseMicros, maximumPulseMicros);
  const upperBound = Math.max(minimumPulseMicros, maximumPulseMicros);

  return {
    maxPulseMicros: upperBound,
    minPulseMicros: lowerBound,
    range: upperBound - lowerBound,
  };
}

function normalizeServoAngleRange(parameters = {}) {
  const minimumAngle = getSimulationParameterNumber(parameters, 'minAngleDegrees', -90);
  const maximumAngle = getSimulationParameterNumber(parameters, 'maxAngleDegrees', 90);

  return {
    idleAngleDegrees: getSimulationParameterNumber(parameters, 'idleAngleDegrees', 0),
    maxAngleDegrees: maximumAngle,
    minAngleDegrees: minimumAngle,
  };
}

function resolveServoSignalDrive({
  parameters = {},
  signalPinName = '',
  simulationPinStates,
} = {}) {
  const signalRange = normalizeServoSignalRange(parameters);
  const pulseRange = normalizeServoPulseRange(parameters);
  const signalPinState = signalPinName ? simulationPinStates?.[signalPinName] : null;

  if (!signalPinState) {
    return {
      driveMode: 'floating',
      normalizedSignal: null,
      pwmDutyCycle: null,
      pwmValue: null,
      servoPulseHighMicros: null,
      servoPulsePeriodMicros: null,
      signalPinState: null,
      signalValue: null,
    };
  }

  let signalValue = null;
  let driveMode = 'floating';
  let servoPulseHighMicros = null;
  let servoPulsePeriodMicros = null;

  if (
    signalPinState.servoPulseDetected &&
    Number.isFinite(Number(signalPinState.servoPulseHighMicros))
  ) {
    servoPulseHighMicros = Number(signalPinState.servoPulseHighMicros);
    servoPulsePeriodMicros = Number.isFinite(Number(signalPinState.servoPulsePeriodMicros))
      ? Number(signalPinState.servoPulsePeriodMicros)
      : null;
    const pulseNormalized = pulseRange.range > VOLTAGE_EPSILON
      ? clampNumber(
          (servoPulseHighMicros - pulseRange.minPulseMicros) / pulseRange.range,
          0,
          1
        )
      : 0.5;

    signalValue = signalRange.minSignalValue + (pulseNormalized * signalRange.range);
    driveMode = 'servo_pulse';
  }

  if (
    signalValue === null &&
    signalPinState.outputMode === 'pwm' &&
    Number.isFinite(Number(signalPinState.pwmDutyCycle))
  ) {
    signalValue = clampNumber(Number(signalPinState.pwmDutyCycle), 0, 1);
    driveMode = 'pwm';
  } else if (signalValue === null && signalPinState.isOutput) {
    signalValue = signalPinState.isHigh
      ? signalRange.maxSignalValue
      : signalRange.minSignalValue;
    driveMode = 'digital';
  }

  if (!Number.isFinite(signalValue)) {
    return {
      driveMode,
      normalizedSignal: null,
      pwmDutyCycle: Number.isFinite(Number(signalPinState.pwmDutyCycle))
        ? clampNumber(Number(signalPinState.pwmDutyCycle), 0, 1)
        : null,
      pwmValue: Number.isFinite(Number(signalPinState.pwmValue))
        ? Number(signalPinState.pwmValue)
        : null,
      servoPulseHighMicros,
      servoPulsePeriodMicros,
      signalPinState,
      signalValue: null,
    };
  }

  const normalizedSignal = signalRange.range > VOLTAGE_EPSILON
    ? clampNumber((signalValue - signalRange.minSignalValue) / signalRange.range, 0, 1)
    : 0.5;

  return {
    driveMode,
    normalizedSignal,
    pwmDutyCycle: Number.isFinite(Number(signalPinState.pwmDutyCycle))
      ? clampNumber(Number(signalPinState.pwmDutyCycle), 0, 1)
      : null,
    pwmValue: Number.isFinite(Number(signalPinState.pwmValue))
      ? Number(signalPinState.pwmValue)
      : null,
    servoPulseHighMicros,
    servoPulsePeriodMicros,
    signalPinState,
    signalValue,
  };
}

function findSimulationModelTerminalPinKeyByRoles(simulationModel, targetRoles = []) {
  const safeTargetRoles = Array.isArray(targetRoles) ? targetRoles : [];

  for (const targetRole of safeTargetRoles) {
    const pinKey = findSimulationModelTerminalPinKey(simulationModel, targetRole);

    if (pinKey) {
      return pinKey;
    }
  }

  return '';
}

function resolveAnalogSourceVoltage({
  endAVoltageState,
  endBVoltageState,
  positionRatio,
} = {}) {
  const safeRatio = clampNumber(Number(positionRatio) || 0, 0, 1);

  if (
    endAVoltageState?.status === 'resolved' &&
    endBVoltageState?.status === 'resolved'
  ) {
    return {
      status: 'resolved',
      voltage:
        (Number(endAVoltageState.voltage) * (1 - safeRatio)) +
        (Number(endBVoltageState.voltage) * safeRatio),
    };
  }

  if (endAVoltageState?.status === 'resolved' && safeRatio <= VOLTAGE_EPSILON) {
    return {
      status: 'resolved',
      voltage: Number(endAVoltageState.voltage),
    };
  }

  if (endBVoltageState?.status === 'resolved' && safeRatio >= 1 - VOLTAGE_EPSILON) {
    return {
      status: 'resolved',
      voltage: Number(endBVoltageState.voltage),
    };
  }

  if (
    endAVoltageState?.status === 'conflict' ||
    endBVoltageState?.status === 'conflict'
  ) {
    return {
      status: 'conflict',
      voltage: null,
    };
  }

  return {
    status: 'floating',
    voltage: null,
  };
}

function buildVariableResistorAnalysis(component, context, simulationModel) {
  const endAPinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['end_a', 'terminal_a']);
  const wiperPinKey = findSimulationModelTerminalPinKeyByRoles(
    simulationModel,
    ['wiper', 'wiper_or_terminal_b']
  );
  const endBPinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['end_b', 'terminal_b']);
  const parameters = simulationModel?.parameters || {};
  const variableResistorState = resolveVariableResistorSimulationState(
    parameters,
    {
      totalResistanceOhms: Number(component?.valueOhms) > 0
        ? Number(component.valueOhms)
        : null,
    }
  );
  const positionState = {
    maxPosition: variableResistorState.maxPosition,
    minPosition: variableResistorState.minPosition,
    position: variableResistorState.position,
    ratio: variableResistorState.positionRatio,
    taper: variableResistorState.taper,
  };
  const resolvedResistanceOhms = variableResistorState.resolvedResistanceOhms;
  const stimulusLabel = String(variableResistorState.stimulusLabel || '').trim() || 'Stimulus';
  const stimulusUnit = String(variableResistorState.stimulusUnit || '').trim();
  const stimulusValue = variableResistorState.stimulusValue;

  if (endAPinKey && wiperPinKey && endBPinKey) {
    const endAReachability = buildTerminalReachability(component.id, endAPinKey, context);
    const wiperReachability = buildTerminalReachability(component.id, wiperPinKey, context);
    const endBReachability = buildTerminalReachability(component.id, endBPinKey, context);
    const endAVoltageState = resolveAnalogVoltageStateFromPotentials(endAReachability.potentials);
    const endBVoltageState = resolveAnalogVoltageStateFromPotentials(endBReachability.potentials);
    const sourceVoltageState = resolveAnalogSourceVoltage({
      endAVoltageState,
      endBVoltageState,
      positionRatio: positionState.ratio,
    });

    return {
      analysisType: 'analog_source',
      componentId: component.id,
      componentName: getComponentDisplayName(component),
      driveDescription: variableResistorState.hasStimulusControl
        ? `Resolved ${stimulusLabel.toLowerCase()} to an equivalent variable-resistor state for the analog source model.`
        : 'Resolved potentiometer wiper voltage from the two end terminals.',
      endAPinKey,
      endBPinKey,
      endAPotentials: endAReachability.potentials,
      endBPotentials: endBReachability.potentials,
      endAVoltage: endAVoltageState.voltage,
      endAVoltageStatus: endAVoltageState.status,
      endBVoltage: endBVoltageState.voltage,
      endBVoltageStatus: endBVoltageState.status,
      position: positionState.position,
      positionRatio: positionState.ratio,
      positionPercent: Math.round(positionState.ratio * 100),
      resolvedResistanceOhms,
      sourceBoardPins: wiperReachability.boardPins,
      sourcePinKey: wiperPinKey,
      sourcePinNames: wiperReachability.pinNames,
      status: sourceVoltageState.status,
      stimulusLabel,
      stimulusUnit,
      stimulusValue: variableResistorState.hasStimulusControl ? stimulusValue : null,
      taper: positionState.taper,
      voltage: sourceVoltageState.voltage,
      wiperPotentials: wiperReachability.potentials,
    };
  }

  if (endAPinKey && wiperPinKey) {
    return {
      analysisType: 'variable_resistor',
      componentId: component.id,
      componentName: getComponentDisplayName(component),
      driveDescription: variableResistorState.hasStimulusControl
        ? `Resolved ${stimulusLabel.toLowerCase()} to a 2-terminal variable resistance.`
        : 'Resolved 2-terminal variable resistor position and resistance.',
      endAPinKey,
      position: positionState.position,
      positionRatio: positionState.ratio,
      positionPercent: Math.round(positionState.ratio * 100),
      resolvedResistanceOhms,
      sourcePinKey: wiperPinKey,
      status: 'resistive_only',
      stimulusLabel,
      stimulusUnit,
      stimulusValue: variableResistorState.hasStimulusControl ? stimulusValue : null,
      taper: positionState.taper,
    };
  }

  return null;
}

function buildServoSimulationAnalysis(component, context, simulationModel) {
  const signalPinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['signal']);
  const powerPinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['power', 'positive']);
  const groundPinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['ground', 'negative']);
  const parameters = simulationModel?.parameters || {};
  const angleRange = normalizeServoAngleRange(parameters);
  const signalReachability = signalPinKey
    ? buildTerminalReachability(component.id, signalPinKey, context)
    : null;
  const powerReachability = powerPinKey
    ? buildTerminalReachability(component.id, powerPinKey, context)
    : null;
  const groundReachability = groundPinKey
    ? buildTerminalReachability(component.id, groundPinKey, context)
    : null;
  const signalPinNames = uniqueValues(signalReachability?.pinNames || []);
  const signalPinName = signalPinNames.length === 1 ? signalPinNames[0] : '';
  const signalDrive = resolveServoSignalDrive({
    parameters,
    signalPinName,
    simulationPinStates: context?.simulationPinStates,
  });
  const powerVoltageState = resolveAnalogVoltageStateFromPotentials(powerReachability?.potentials || []);
  const groundVoltageState = resolveAnalogVoltageStateFromPotentials(groundReachability?.potentials || []);
  const powerVoltage = Number(powerVoltageState?.voltage);
  const groundVoltage = Number(groundVoltageState?.voltage);
  const hasResolvedPower = powerVoltageState?.status === 'resolved' && Number.isFinite(powerVoltage);
  const hasResolvedGround = groundVoltageState?.status === 'resolved' && Number.isFinite(groundVoltage);
  const isPowered = hasResolvedPower &&
    hasResolvedGround &&
    (powerVoltage - groundVoltage) > 0.5;
  const normalizedSignal = Number.isFinite(Number(signalDrive.normalizedSignal))
    ? clampNumber(Number(signalDrive.normalizedSignal), 0, 1)
    : null;
  const angleDegrees = normalizedSignal === null || !isPowered
    ? angleRange.idleAngleDegrees
    : angleRange.minAngleDegrees +
      ((angleRange.maxAngleDegrees - angleRange.minAngleDegrees) * normalizedSignal);
  let driveDescription = 'Servo signal is not resolved to a single Arduino output pin yet.';

  if (!isPowered) {
    driveDescription = 'Servo is not fully powered yet; connect V+ and GND to drive the horn.';
  } else if (signalDrive.driveMode === 'servo_pulse' && signalPinName) {
    driveDescription = `Servo horn follows Servo library pulse timing from ${signalPinName}.`;
  } else if (signalDrive.driveMode === 'pwm' && signalPinName) {
    driveDescription = `Servo horn follows PWM duty cycle from ${signalPinName}.`;
  } else if (signalDrive.driveMode === 'digital' && signalPinName) {
    driveDescription = `Servo horn is using digital HIGH/LOW from ${signalPinName}.`;
  }

  return {
    analysisType: 'servo',
    angleDegrees,
    componentId: component.id,
    componentName: getComponentDisplayName(component),
    driveDescription,
    driveMode: signalDrive.driveMode,
    groundBoardPins: groundReachability?.boardPins || [],
    groundPinKey,
    groundPotentials: groundReachability?.potentials || [],
    groundVoltage: hasResolvedGround ? groundVoltage : null,
    groundVoltageStatus: groundVoltageState?.status || 'floating',
    isPowered,
    normalizedSignal,
    powerBoardPins: powerReachability?.boardPins || [],
    powerPinKey,
    powerPotentials: powerReachability?.potentials || [],
    powerVoltage: hasResolvedPower ? powerVoltage : null,
    powerVoltageStatus: powerVoltageState?.status || 'floating',
    pwmDutyCycle: signalDrive.pwmDutyCycle,
    pwmValue: signalDrive.pwmValue,
    servoPulseHighMicros: signalDrive.servoPulseHighMicros,
    servoPulsePeriodMicros: signalDrive.servoPulsePeriodMicros,
    signalBoardPins: signalReachability?.boardPins || [],
    signalPinKey,
    signalPinName,
    signalPinNames,
    signalPotentials: signalReachability?.potentials || [],
  };
}

function buildBuzzerSimulationAnalysis(component, context, simulationModel) {
  const signalPinKey = findSimulationModelTerminalPinKeyByRoles(
    simulationModel,
    ['signal', 'positive', 'anode']
  );
  const groundPinKey = findSimulationModelTerminalPinKeyByRoles(
    simulationModel,
    ['ground', 'negative', 'cathode']
  );

  if (!signalPinKey || !groundPinKey) {
    return null;
  }

  const signalReachability = buildTerminalReachability(component.id, signalPinKey, context);
  const groundReachability = buildTerminalReachability(component.id, groundPinKey, context);
  const signalPinNames = uniqueValues(signalReachability?.pinNames || []);
  const signalPinName = signalPinNames.length === 1 ? signalPinNames[0] : '';
  const signalPinState = signalPinName ? context?.simulationPinStates?.[signalPinName] : null;
  const toneFrequencyHz = Number(signalPinState?.toneFrequencyHz);
  const groundRails = uniqueValues(groundReachability?.rails || []);
  const isGrounded = groundRails.includes('GND');
  const isActive = isGrounded &&
    Boolean(signalPinName) &&
    Boolean(signalPinState?.toneDetected) &&
    Number.isFinite(toneFrequencyHz);
  let driveMode = 'floating';
  let driveDescription = 'Buzzer positive terminal is not resolved to a single Arduino output pin yet.';

  if (signalPinState?.toneDetected && Number.isFinite(toneFrequencyHz)) {
    driveMode = 'tone';
  } else if (signalPinState?.outputMode === 'pwm') {
    driveMode = 'pwm';
  } else if (signalPinState?.isOutput) {
    driveMode = 'digital';
  } else if (signalPinState) {
    driveMode = signalPinState.outputMode || 'input';
  }

  if (!isGrounded) {
    driveDescription = 'Buzzer negative terminal must return to GND before it can sound.';
  } else if (!signalPinName) {
    driveDescription = 'Buzzer positive terminal must resolve to one Arduino GPIO pin before it can sound.';
  } else if (isActive) {
    driveDescription = `Passive buzzer is sounding from ${signalPinName} at about ${Math.round(toneFrequencyHz)} Hz.`;
  } else if (signalPinState?.outputMode === 'pwm') {
    driveDescription = `Buzzer is wired to ${signalPinName}, but no stable audible tone has been detected yet.`;
  } else if (signalPinState?.isOutput) {
    driveDescription = `Buzzer is wired to ${signalPinName}, but a steady HIGH or LOW output stays silent.`;
  } else {
    driveDescription = `Buzzer is wired to ${signalPinName}, but the pin is not currently driving an output signal.`;
  }

  return {
    analysisType: 'buzzer',
    componentId: component.id,
    componentName: getComponentDisplayName(component),
    driveDescription,
    driveMode,
    estimatedFrequencyHz: isActive ? toneFrequencyHz : null,
    groundBoardPins: groundReachability?.boardPins || [],
    groundPinKey,
    groundPotentials: groundReachability?.potentials || [],
    groundRails,
    isActive,
    isGrounded,
    signalBoardPins: signalReachability?.boardPins || [],
    signalIsOutput: Boolean(signalPinState?.isOutput),
    signalOutputMode: String(signalPinState?.outputMode || 'input'),
    signalPinKey,
    signalPinName,
    signalPinNames,
    signalPotentials: signalReachability?.potentials || [],
    signalState: signalPinState?.state || 'floating',
    toneAgeMicros: Number.isFinite(Number(signalPinState?.toneAgeMicros))
      ? Number(signalPinState.toneAgeMicros)
      : null,
    toneEdgeSampleCount: Number(signalPinState?.toneEdgeSampleCount) || 0,
  };
}

function buildDigitalSignalSource(reachability) {
  const pinNames = uniqueValues(reachability?.pinNames || []);
  const digitalState = resolveDigitalInputStateFromPotentials(
    reachability?.potentials || []
  );

  return {
    boardPins: reachability?.boardPins || [],
    level: digitalState?.level ?? null,
    pinName: pinNames.length === 1 ? pinNames[0] : '',
    pinNames,
    potentials: reachability?.potentials || [],
    rails: uniqueValues(reachability?.rails || []),
    status: digitalState?.status || 'floating',
  };
}

function isResolvedDigitalSignalSource(source) {
  if (!source || typeof source !== 'object') {
    return false;
  }

  if (source.pinName) {
    return true;
  }

  return source.status === 'high' || source.status === 'low';
}

function buildSevenSegmentTerminalSource(component, context, pinKey) {
  const reachability = pinKey
    ? buildTerminalReachability(component.id, pinKey, context)
    : null;

  return {
    ...buildDigitalSignalSource(reachability),
    terminalPinKey: pinKey,
  };
}

function buildSevenSegmentSimulationAnalysis(component, context, simulationModel) {
  const digitCount = normalizeSevenSegmentDigitCount(
    component?.digitCount ?? simulationModel?.parameters?.digitCount
  );
  const commonType = normalizeSevenSegmentCommonType(
    component?.commonType ?? simulationModel?.parameters?.commonType
  );
  const activeSegmentLevel = commonType === 'common_anode' ? 'low' : 'high';
  const activeDigitLevel = commonType === 'common_anode' ? 'high' : 'low';
  const digitPinKeys = getSevenSegmentDigitPinKeys(digitCount);
  const segmentPinKeyBySegment = Object.fromEntries(
    ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'dp'].map((segmentKey) => {
      const fallbackPinKey = getSevenSegmentSegmentPinKey(segmentKey);
      const resolvedPinKey = findSimulationModelTerminalPinKeyByRoles(
        simulationModel,
        [`segment_${segmentKey}`]
      ) || fallbackPinKey;

      return [segmentKey, resolvedPinKey];
    })
  );
  const digitTerminalPinKeyByDigit = Object.fromEntries(
    digitPinKeys.map((pinKey, index) => {
      const resolvedPinKey = findSimulationModelTerminalPinKeyByRoles(
        simulationModel,
        [`digit_${index + 1}`, `digit_common_${index + 1}`]
      ) || pinKey;

      return [pinKey, resolvedPinKey];
    })
  );
  const segmentPins = Object.fromEntries(
    Object.entries(segmentPinKeyBySegment).map(([segmentKey, pinKey]) => [
      getSevenSegmentSegmentPinKey(segmentKey),
      buildSevenSegmentTerminalSource(component, context, pinKey),
    ])
  );
  const digitPins = Object.fromEntries(
    digitPinKeys.map((pinKey) => [
      pinKey,
      buildSevenSegmentTerminalSource(
        component,
        context,
        digitTerminalPinKeyByDigit[pinKey]
      ),
    ])
  );
  const activeSegmentPins = Object.entries(segmentPins)
    .filter(([, source]) => source?.status === activeSegmentLevel)
    .map(([pinKey]) => pinKey);
  const activeDigitPins = Object.entries(digitPins)
    .filter(([, source]) => source?.status === activeDigitLevel)
    .map(([pinKey]) => pinKey);
  const isPowered = activeSegmentPins.length > 0 && activeDigitPins.length > 0;
  const segmentStates = buildSevenSegmentSegmentStates(digitCount).map((digitState, index) => {
    const digitPinKey = digitPinKeys[index];
    const digitSource = digitPins[digitPinKey];

    if (digitSource?.status !== activeDigitLevel) {
      return digitState;
    }

    return Object.fromEntries(
      Object.keys(digitState).map((segmentKey) => {
        const pinKey = getSevenSegmentSegmentPinKey(segmentKey);
        return [segmentKey, segmentPins[pinKey]?.status === activeSegmentLevel];
      })
    );
  });
  const commonTypeLabel = getSevenSegmentCommonTypeLabel(commonType);
  let driveDescription = commonType === 'common_anode'
    ? 'Connect a digit common pin HIGH and pull segment pins A-G/DP LOW to light the selected segments.'
    : 'Connect segment pins A-G/DP HIGH and pull a DIG pin LOW to light that digit.';

  if (isPowered) {
    driveDescription = commonType === 'common_anode'
      ? 'Resolved common-anode seven-segment drive from LOW-active segments and HIGH-active digit commons.'
      : 'Resolved common-cathode seven-segment drive from HIGH-active segments and LOW-active digit commons.';
  } else if (
    Object.values(segmentPins).some(isResolvedDigitalSignalSource) ||
    Object.values(digitPins).some(isResolvedDigitalSignalSource)
  ) {
    driveDescription = commonType === 'common_anode'
      ? 'Seven-segment pins are connected, but no active LOW segment plus HIGH digit-common path is resolved yet.'
      : 'Seven-segment pins are connected, but no active HIGH segment plus LOW digit-common path is resolved yet.';
  }

  return {
    analysisType: 'seven_segment_display',
    componentId: component.id,
    componentName: getComponentDisplayName(component),
    commonType,
    commonTypeLabel,
    digitCount,
    driveDescription,
    isPowered,
    activeDigitLevel,
    segmentStates,
    activeDigitPins,
    activeSegmentLevel,
    activeSegmentPins,
    digitPins,
    segmentPins,
    terminalPinKeys: {
      digits: digitTerminalPinKeyByDigit,
      segments: Object.fromEntries(
        Object.entries(segmentPins).map(([pinKey, source]) => [
          getSevenSegmentSegmentKeyByPin(pinKey) || pinKey,
          source?.terminalPinKey || pinKey,
        ])
      ),
    },
  };
}

function buildLcd16x2SimulationAnalysis(component, context, simulationModel) {
  const powerPinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['power', 'positive']);
  const groundPinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['ground', 'negative']);
  const rsPinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['register_select']);
  const rwPinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['read_write']);
  const enablePinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['enable']);
  const d4PinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['data_bit_4']);
  const d5PinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['data_bit_5']);
  const d6PinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['data_bit_6']);
  const d7PinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['data_bit_7']);
  const backlightAnodePinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['backlight_anode']);
  const backlightCathodePinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['backlight_cathode']);

  if (
    !powerPinKey ||
    !groundPinKey ||
    !rsPinKey ||
    !rwPinKey ||
    !enablePinKey ||
    !d4PinKey ||
    !d5PinKey ||
    !d6PinKey ||
    !d7PinKey
  ) {
    return null;
  }

  const powerReachability = buildTerminalReachability(component.id, powerPinKey, context);
  const groundReachability = buildTerminalReachability(component.id, groundPinKey, context);
  const rsReachability = buildTerminalReachability(component.id, rsPinKey, context);
  const rwReachability = buildTerminalReachability(component.id, rwPinKey, context);
  const enableReachability = buildTerminalReachability(component.id, enablePinKey, context);
  const d4Reachability = buildTerminalReachability(component.id, d4PinKey, context);
  const d5Reachability = buildTerminalReachability(component.id, d5PinKey, context);
  const d6Reachability = buildTerminalReachability(component.id, d6PinKey, context);
  const d7Reachability = buildTerminalReachability(component.id, d7PinKey, context);
  const backlightAnodeReachability = backlightAnodePinKey
    ? buildTerminalReachability(component.id, backlightAnodePinKey, context)
    : null;
  const backlightCathodeReachability = backlightCathodePinKey
    ? buildTerminalReachability(component.id, backlightCathodePinKey, context)
    : null;
  const powerVoltageState = resolveAnalogVoltageStateFromPotentials(powerReachability?.potentials || []);
  const groundVoltageState = resolveAnalogVoltageStateFromPotentials(groundReachability?.potentials || []);
  const powerVoltage = Number(powerVoltageState?.voltage);
  const groundVoltage = Number(groundVoltageState?.voltage);
  const hasResolvedPower = powerVoltageState?.status === 'resolved' && Number.isFinite(powerVoltage);
  const hasResolvedGround = groundVoltageState?.status === 'resolved' && Number.isFinite(groundVoltage);
  const isPowered = hasResolvedPower &&
    hasResolvedGround &&
    (powerVoltage - groundVoltage) > 0.5;
  const signals = {
    backlightAnode: buildDigitalSignalSource(backlightAnodeReachability),
    backlightCathode: buildDigitalSignalSource(backlightCathodeReachability),
    d4: buildDigitalSignalSource(d4Reachability),
    d5: buildDigitalSignalSource(d5Reachability),
    d6: buildDigitalSignalSource(d6Reachability),
    d7: buildDigitalSignalSource(d7Reachability),
    e: buildDigitalSignalSource(enableReachability),
    rs: buildDigitalSignalSource(rsReachability),
    rw: buildDigitalSignalSource(rwReachability),
  };
  const rwPinState = signals.rw.pinName
    ? context?.simulationPinStates?.[signals.rw.pinName]
    : null;
  const is4BitBusResolved = [signals.rs, signals.e, signals.d4, signals.d5, signals.d6, signals.d7]
    .every(isResolvedDigitalSignalSource);
  const isWriteCapable = signals.rw.status === 'low' || (
    rwPinState &&
    (
      rwPinState.sampledHigh === false ||
      rwPinState.isHigh === false ||
      String(rwPinState.logicLevel || '').trim().toLowerCase() === 'low'
    )
  );
  let driveDescription = 'LCD control pins must resolve to single Arduino GPIO signals before text can render.';

  if (!isPowered) {
    driveDescription = 'LCD is not fully powered yet; connect VDD and VSS/GND to simulate the display.';
  } else if (!is4BitBusResolved) {
    driveDescription = 'LCD RS, E, and D4-D7 should each resolve cleanly to one digital signal for 4-bit simulation.';
  } else if (!isWriteCapable) {
    driveDescription = 'LCD RW should be tied LOW or driven LOW so the simulator can treat the module as write-only.';
  } else {
    driveDescription = 'LCD is ready for HD44780-style 4-bit writes.';
  }

  return {
    analysisType: 'lcd_16x2',
    componentId: component.id,
    componentName: getComponentDisplayName(component),
    driveDescription,
    groundBoardPins: groundReachability?.boardPins || [],
    groundPinKey,
    groundPotentials: groundReachability?.potentials || [],
    groundVoltage: hasResolvedGround ? groundVoltage : null,
    groundVoltageStatus: groundVoltageState?.status || 'floating',
    is4BitBusResolved,
    isPowered,
    isWriteCapable,
    parameters: simulationModel?.parameters || {},
    powerBoardPins: powerReachability?.boardPins || [],
    powerPinKey,
    powerPotentials: powerReachability?.potentials || [],
    powerVoltage: hasResolvedPower ? powerVoltage : null,
    powerVoltageStatus: powerVoltageState?.status || 'floating',
    signals,
    terminalPinKeys: {
      backlightAnode: backlightAnodePinKey || '',
      backlightCathode: backlightCathodePinKey || '',
      d4: d4PinKey,
      d5: d5PinKey,
      d6: d6PinKey,
      d7: d7PinKey,
      e: enablePinKey,
      ground: groundPinKey,
      power: powerPinKey,
      rs: rsPinKey,
      rw: rwPinKey,
    },
  };
}

function normalizeResistanceOhms(value, fallbackValue = null) {
  const numericValue = Number(value);
  const numericFallbackValue = Number(fallbackValue);
  const resolvedValue = (
    Number.isFinite(numericValue) && numericValue > 0
      ? numericValue
      : (
          Number.isFinite(numericFallbackValue) && numericFallbackValue > 0
            ? numericFallbackValue
            : null
        )
  );

  if (!Number.isFinite(resolvedValue) || resolvedValue <= 0) {
    return null;
  }

  return Math.max(resolvedValue, MIN_SIMULATED_RESISTANCE_OHMS);
}

function buildResistiveElement(component, fromPinKey, toPinKey, resistanceOhms, metadata = {}) {
  const safeResistanceOhms = normalizeResistanceOhms(resistanceOhms);

  if (!component?.id || !fromPinKey || !toPinKey || !safeResistanceOhms) {
    return null;
  }

  return {
    componentId: component.id,
    componentName: getComponentDisplayName(component),
    fromEndpointId: buildEndpointId(component.id, fromPinKey),
    fromPinKey,
    modelKey: String(metadata?.modelKey || '').trim(),
    resistanceOhms: safeResistanceOhms,
    toEndpointId: buildEndpointId(component.id, toPinKey),
    toPinKey,
  };
}

function buildPassiveResistiveEdges(component) {
  const model = getPassiveSimulationModel(component);
  const simulationModel = model?.simulationModel;

  if (!simulationModel) {
    return [];
  }

  const modelKey = String(simulationModel?.modelKey || '').trim();
  const behavior = String(simulationModel?.behavior || '').trim();
  const parameters = simulationModel?.parameters || {};

  if (component?.passiveType === 'resistor' || modelKey === 'resistor') {
    const [fromPinKey = 'A', toPinKey = 'B'] = Object.keys(simulationModel?.terminalRoles || {});
    const resistanceOhms = normalizeResistanceOhms(
      component?.valueOhms,
      getSimulationParameterNumber(parameters, 'resistanceOhms', 10000)
    );

    return resistanceOhms
      ? [buildResistiveElement(component, fromPinKey, toPinKey, resistanceOhms, { modelKey: 'resistor' })].filter(Boolean)
      : [];
  }

  if (behavior !== 'variable_resistor' && modelKey !== 'potentiometer' && modelKey !== 'variable_resistor') {
    return [];
  }

  const endAPinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['end_a', 'terminal_a']);
  const wiperPinKey = findSimulationModelTerminalPinKeyByRoles(
    simulationModel,
    ['wiper', 'wiper_or_terminal_b']
  );
  const endBPinKey = findSimulationModelTerminalPinKeyByRoles(simulationModel, ['end_b', 'terminal_b']);
  const variableResistorState = resolveVariableResistorSimulationState(
    parameters,
    {
      totalResistanceOhms: Number(component?.valueOhms) > 0
        ? Number(component.valueOhms)
        : null,
    }
  );
  const totalResistanceOhms = normalizeResistanceOhms(
    variableResistorState.totalResistanceOhms,
    10000
  );

  if (!totalResistanceOhms || !endAPinKey || !wiperPinKey) {
    return [];
  }

  if (endBPinKey) {
    return [
      buildResistiveElement(
        component,
        endAPinKey,
        wiperPinKey,
        totalResistanceOhms * variableResistorState.positionRatio,
        { modelKey: modelKey || 'potentiometer' }
      ),
      buildResistiveElement(
        component,
        wiperPinKey,
        endBPinKey,
        totalResistanceOhms * (1 - variableResistorState.positionRatio),
        { modelKey: modelKey || 'potentiometer' }
      ),
    ].filter(Boolean);
  }

  return [
    buildResistiveElement(
      component,
      endAPinKey,
      wiperPinKey,
      variableResistorState.resolvedResistanceOhms,
      { modelKey: modelKey || 'variable_resistor' }
    ),
  ].filter(Boolean);
}

function collectKnownEndpointIds(connections, suggestionPins, extraEndpointIds = []) {
  const endpointIds = new Set();
  const addEndpointId = (endpointId) => {
    const safeEndpointId = String(endpointId || '').trim();

    if (!safeEndpointId) {
      return;
    }

    endpointIds.add(safeEndpointId);
  };

  (Array.isArray(connections) ? connections : []).forEach((connection) => {
    addEndpointId(buildEndpointId(connection?.from?.partId, connection?.from?.pinKey));
    addEndpointId(buildEndpointId(connection?.to?.partId, connection?.to?.pinKey));
  });

  (Array.isArray(suggestionPins) ? suggestionPins : []).forEach((pin) => {
    const componentId = String(pin?.component_id || '').trim();
    const markerKey = String(pin?.marker_key || pin?.markerKey || '').trim();

    if (!componentId || !markerKey) {
      return;
    }

    addEndpointId(buildEndpointId(componentId, markerKey));

    getPinCandidateTokens(pin).forEach((token) => {
      addEndpointId(buildEndpointId(componentId, token));
    });

    const markerBase = stripMarkerIndex(markerKey);

    if (markerBase) {
      addEndpointId(buildEndpointId(componentId, markerBase));
    }
  });

  (Array.isArray(extraEndpointIds) ? extraEndpointIds : []).forEach(addEndpointId);

  return Array.from(endpointIds);
}

function buildEndpointNodeMap(adjacency, endpointIds = []) {
  const endpointNodeIds = new Map();
  const nodeEndpointIds = new Map();
  const seedEndpointIds = uniqueValues([
    ...Array.from(adjacency?.keys?.() || []),
    ...(Array.isArray(endpointIds) ? endpointIds : []),
  ]);
  let nextNodeIndex = 0;

  seedEndpointIds.forEach((startEndpointId) => {
    if (!startEndpointId || endpointNodeIds.has(startEndpointId)) {
      return;
    }

    const nodeId = `node_${nextNodeIndex++}`;
    const memberEndpointIds = new Set([startEndpointId]);
    const queue = [startEndpointId];

    endpointNodeIds.set(startEndpointId, nodeId);

    while (queue.length > 0) {
      const currentEndpointId = queue.shift();
      const neighbors = adjacency?.get(currentEndpointId);

      if (!neighbors) {
        continue;
      }

      neighbors.forEach((neighborEndpointId) => {
        if (!neighborEndpointId || endpointNodeIds.has(neighborEndpointId)) {
          return;
        }

        endpointNodeIds.set(neighborEndpointId, nodeId);
        memberEndpointIds.add(neighborEndpointId);
        queue.push(neighborEndpointId);
      });
    }

    nodeEndpointIds.set(nodeId, memberEndpointIds);
  });

  return {
    endpointNodeIds,
    nodeEndpointIds,
  };
}

function buildFixedNodeVoltageStates(
  nodeEndpointIds,
  endpointPinResolver,
  unoComponentIds,
  simulationPinStates
) {
  const fixedNodeVoltageStates = new Map();

  nodeEndpointIds.forEach((endpointIds, nodeId) => {
    const voltages = [];
    const sources = [];

    endpointIds.forEach((endpointId) => {
      const resolvedSource = resolveEndpointDrivenVoltage(
        resolveEndpointPinFromId(endpointId, endpointPinResolver),
        unoComponentIds,
        simulationPinStates
      );

      if (!resolvedSource || !Number.isFinite(Number(resolvedSource.voltage))) {
        return;
      }

      const safeVoltage = Number(resolvedSource.voltage);

      if (!voltages.some((knownVoltage) => isApproximatelyEqual(knownVoltage, safeVoltage))) {
        voltages.push(safeVoltage);
      }

      if (!sources.some((source) => (
        source.label === resolvedSource.label &&
        isApproximatelyEqual(source.voltage, safeVoltage)
      ))) {
        sources.push({
          label: resolvedSource.label,
          level: resolvedSource.level,
          voltage: safeVoltage,
        });
      }
    });

    if (voltages.length === 0) {
      fixedNodeVoltageStates.set(nodeId, {
        sources,
        status: 'floating',
        voltage: null,
      });
      return;
    }

    if (voltages.length === 1) {
      fixedNodeVoltageStates.set(nodeId, {
        sources,
        status: 'resolved',
        voltage: voltages[0],
      });
      return;
    }

    fixedNodeVoltageStates.set(nodeId, {
      sources,
      status: 'conflict',
      voltage: null,
    });
  });

  return fixedNodeVoltageStates;
}

function collectResistiveConnectedNodes(startNodeId, resistiveEdgesByNode, resistiveEdges) {
  if (!startNodeId) {
    return {
      edgeIndices: new Set(),
      nodeIds: new Set(),
    };
  }

  const nodeIds = new Set([startNodeId]);
  const edgeIndices = new Set();
  const queue = [startNodeId];

  while (queue.length > 0) {
    const nodeId = queue.shift();
    const incidentEdgeIndices = resistiveEdgesByNode.get(nodeId) || new Set();

    incidentEdgeIndices.forEach((edgeIndex) => {
      const edge = resistiveEdges[edgeIndex];

      if (!edge) {
        return;
      }

      edgeIndices.add(edgeIndex);

      const neighborNodeId = edge.fromNodeId === nodeId
        ? edge.toNodeId
        : edge.fromNodeId;

      if (!neighborNodeId || nodeIds.has(neighborNodeId)) {
        return;
      }

      nodeIds.add(neighborNodeId);
      queue.push(neighborNodeId);
    });
  }

  return {
    edgeIndices,
    nodeIds,
  };
}

function solveLinearEquationSystem(matrix, constants) {
  const size = Array.isArray(matrix) ? matrix.length : 0;

  if (size === 0 || !Array.isArray(constants) || constants.length !== size) {
    return null;
  }

  const augmentedMatrix = matrix.map((row, rowIndex) => (
    [...row, constants[rowIndex]]
  ));

  for (let pivotIndex = 0; pivotIndex < size; pivotIndex += 1) {
    let bestRowIndex = pivotIndex;

    for (let rowIndex = pivotIndex + 1; rowIndex < size; rowIndex += 1) {
      if (Math.abs(augmentedMatrix[rowIndex][pivotIndex]) > Math.abs(augmentedMatrix[bestRowIndex][pivotIndex])) {
        bestRowIndex = rowIndex;
      }
    }

    if (Math.abs(augmentedMatrix[bestRowIndex][pivotIndex]) <= VOLTAGE_EPSILON) {
      return null;
    }

    if (bestRowIndex !== pivotIndex) {
      [
        augmentedMatrix[pivotIndex],
        augmentedMatrix[bestRowIndex],
      ] = [
        augmentedMatrix[bestRowIndex],
        augmentedMatrix[pivotIndex],
      ];
    }

    const pivotValue = augmentedMatrix[pivotIndex][pivotIndex];

    for (let columnIndex = pivotIndex; columnIndex <= size; columnIndex += 1) {
      augmentedMatrix[pivotIndex][columnIndex] /= pivotValue;
    }

    for (let rowIndex = 0; rowIndex < size; rowIndex += 1) {
      if (rowIndex === pivotIndex) {
        continue;
      }

      const factor = augmentedMatrix[rowIndex][pivotIndex];

      if (Math.abs(factor) <= VOLTAGE_EPSILON) {
        continue;
      }

      for (let columnIndex = pivotIndex; columnIndex <= size; columnIndex += 1) {
        augmentedMatrix[rowIndex][columnIndex] -= factor * augmentedMatrix[pivotIndex][columnIndex];
      }
    }
  }

  return augmentedMatrix.map((row) => row[size]);
}

function buildResistiveSourceAnalyses(edgeIndices, resistiveEdges) {
  const analysesByComponentId = new Map();

  edgeIndices.forEach((edgeIndex) => {
    const edge = resistiveEdges[edgeIndex];

    if (!edge?.componentId || analysesByComponentId.has(edge.componentId)) {
      return;
    }

    analysesByComponentId.set(edge.componentId, {
      analysisType: 'resistive_network',
      componentId: edge.componentId,
      componentName: edge.componentName || 'Passive Component',
      modelKey: edge.modelKey || '',
    });
  });

  return Array.from(analysesByComponentId.values());
}

function buildAnalogResistiveNetwork({
  components,
  connections,
  suggestionPins,
  simulationPinStates,
  simulationRuntimeState,
  endpointPinResolver,
  unoComponentIds,
} = {}) {
  const hardAdjacency = buildHardCircuitAdjacency(
    components,
    connections,
    suggestionPins,
    simulationRuntimeState
  );
  const resistiveEdges = (Array.isArray(components) ? components : [])
    .flatMap((component) => buildPassiveResistiveEdges(component));
  const knownEndpointIds = collectKnownEndpointIds(
    connections,
    suggestionPins,
    resistiveEdges.flatMap((edge) => [edge.fromEndpointId, edge.toEndpointId])
  );
  const {
    endpointNodeIds,
    nodeEndpointIds,
  } = buildEndpointNodeMap(hardAdjacency, knownEndpointIds);
  const fixedNodeVoltageStates = buildFixedNodeVoltageStates(
    nodeEndpointIds,
    endpointPinResolver,
    unoComponentIds,
    simulationPinStates
  );
  const resolvedResistiveEdges = [];
  const resistiveEdgesByNode = new Map();

  resistiveEdges.forEach((edge) => {
    const fromNodeId = endpointNodeIds.get(edge.fromEndpointId);
    const toNodeId = endpointNodeIds.get(edge.toEndpointId);

    if (!fromNodeId || !toNodeId || fromNodeId === toNodeId) {
      return;
    }

    const edgeIndex = resolvedResistiveEdges.length;
    const resolvedEdge = {
      ...edge,
      fromNodeId,
      toNodeId,
    };

    resolvedResistiveEdges.push(resolvedEdge);

    if (!resistiveEdgesByNode.has(fromNodeId)) {
      resistiveEdgesByNode.set(fromNodeId, new Set());
    }
    if (!resistiveEdgesByNode.has(toNodeId)) {
      resistiveEdgesByNode.set(toNodeId, new Set());
    }

    resistiveEdgesByNode.get(fromNodeId).add(edgeIndex);
    resistiveEdgesByNode.get(toNodeId).add(edgeIndex);
  });

  return {
    endpointNodeIds,
    fixedNodeVoltageStates,
    hardAdjacency,
    nodeEndpointIds,
    resistiveEdges: resolvedResistiveEdges,
    resistiveEdgesByNode,
  };
}

function analyzeArduinoUnoAnalogPinViaResistiveNetwork(startEndpointIds, resistiveNetwork) {
  const targetNodeId = uniqueValues(
    (Array.isArray(startEndpointIds) ? startEndpointIds : [])
      .map((endpointId) => resistiveNetwork?.endpointNodeIds?.get(endpointId))
      .filter(Boolean)
  )[0] || '';

  if (!targetNodeId) {
    return {
      sourceAnalyses: [],
      status: 'floating',
      voltage: null,
    };
  }

  const targetNodeVoltageState = resistiveNetwork?.fixedNodeVoltageStates?.get(targetNodeId);

  if (targetNodeVoltageState?.status === 'conflict') {
    return {
      sourceAnalyses: [],
      status: 'conflict',
      voltage: null,
    };
  }

  if (targetNodeVoltageState?.status === 'resolved') {
    return {
      sourceAnalyses: [],
      status: 'resolved',
      voltage: targetNodeVoltageState.voltage,
    };
  }

  const connectedNetwork = collectResistiveConnectedNodes(
    targetNodeId,
    resistiveNetwork?.resistiveEdgesByNode,
    resistiveNetwork?.resistiveEdges || []
  );

  if (connectedNetwork.edgeIndices.size === 0) {
    return {
      sourceAnalyses: [],
      status: 'floating',
      voltage: null,
    };
  }

  const connectedNodeIds = Array.from(connectedNetwork.nodeIds);

  if (connectedNodeIds.some((nodeId) => (
    resistiveNetwork?.fixedNodeVoltageStates?.get(nodeId)?.status === 'conflict'
  ))) {
    return {
      sourceAnalyses: buildResistiveSourceAnalyses(
        connectedNetwork.edgeIndices,
        resistiveNetwork?.resistiveEdges || []
      ),
      status: 'conflict',
      voltage: null,
    };
  }

  const fixedNodeIds = connectedNodeIds.filter((nodeId) => (
    resistiveNetwork?.fixedNodeVoltageStates?.get(nodeId)?.status === 'resolved'
  ));

  if (fixedNodeIds.length === 0) {
    return {
      sourceAnalyses: buildResistiveSourceAnalyses(
        connectedNetwork.edgeIndices,
        resistiveNetwork?.resistiveEdges || []
      ),
      status: 'floating',
      voltage: null,
    };
  }

  const unknownNodeIds = connectedNodeIds.filter((nodeId) => !fixedNodeIds.includes(nodeId));
  const sourceAnalyses = buildResistiveSourceAnalyses(
    connectedNetwork.edgeIndices,
    resistiveNetwork?.resistiveEdges || []
  );

  if (unknownNodeIds.length === 0) {
    return {
      sourceAnalyses,
      status: 'floating',
      voltage: null,
    };
  }

  const unknownNodeIndexById = new Map(
    unknownNodeIds.map((nodeId, index) => [nodeId, index])
  );
  const matrix = unknownNodeIds.map(() => unknownNodeIds.map(() => 0));
  const constants = unknownNodeIds.map(() => 0);

  for (const nodeId of unknownNodeIds) {
    const nodeIndex = unknownNodeIndexById.get(nodeId);
    const incidentEdgeIndices = resistiveNetwork?.resistiveEdgesByNode?.get(nodeId) || new Set();

    incidentEdgeIndices.forEach((edgeIndex) => {
      if (!connectedNetwork.edgeIndices.has(edgeIndex)) {
        return;
      }

      const edge = resistiveNetwork?.resistiveEdges?.[edgeIndex];

      if (!edge) {
        return;
      }

      const conductance = 1 / Math.max(Number(edge.resistanceOhms) || MIN_SIMULATED_RESISTANCE_OHMS, MIN_SIMULATED_RESISTANCE_OHMS);
      const neighborNodeId = edge.fromNodeId === nodeId
        ? edge.toNodeId
        : edge.fromNodeId;

      matrix[nodeIndex][nodeIndex] += conductance;

      if (unknownNodeIndexById.has(neighborNodeId)) {
        matrix[nodeIndex][unknownNodeIndexById.get(neighborNodeId)] -= conductance;
        return;
      }

      const neighborVoltageState = resistiveNetwork?.fixedNodeVoltageStates?.get(neighborNodeId);

      if (neighborVoltageState?.status === 'resolved') {
        constants[nodeIndex] += conductance * Number(neighborVoltageState.voltage);
      }
    });

    if (matrix[nodeIndex][nodeIndex] <= VOLTAGE_EPSILON) {
      return {
        sourceAnalyses,
        status: 'floating',
        voltage: null,
      };
    }
  }

  const solvedVoltages = solveLinearEquationSystem(matrix, constants);

  if (!solvedVoltages) {
    return {
      sourceAnalyses,
      status: 'floating',
      voltage: null,
    };
  }

  const targetVoltage = solvedVoltages[unknownNodeIndexById.get(targetNodeId)];

  return Number.isFinite(targetVoltage)
    ? {
        sourceAnalyses,
        status: 'resolved',
        voltage: targetVoltage,
      }
    : {
        sourceAnalyses,
        status: 'floating',
        voltage: null,
      };
}

function buildTerminalReachability(componentId, pinKey, context) {
  return buildTerminalReachabilityFromEndpointIds(
    [buildEndpointId(componentId, pinKey)],
    context
  );
}

function buildTerminalReachabilityFromEndpointIds(
  startEndpointIds,
  context,
  adjacency = context?.adjacency
) {
  const reachableEndpoints = collectReachableEndpointsFromStarts(
    startEndpointIds,
    adjacency
  );

  return {
    reachableEndpoints,
    potentials: collectReachablePotentials(
      reachableEndpoints,
      context.endpointPinResolver,
      context.unoComponentIds,
      context.simulationPinStates
    ),
    pinNames: collectReachableArduinoPinNames(
      reachableEndpoints,
      context.endpointPinResolver,
      context.unoComponentIds
    ),
    rails: collectReachableArduinoRailLabels(
      reachableEndpoints,
      context.endpointPinResolver,
      context.unoComponentIds
    ),
    boardPins: collectReachableBoardPinDetails(
      reachableEndpoints,
      context.endpointPinResolver,
      context.unoComponentIds,
      context.componentsById
    ),
  };
}

function findArduinoUnoBoardPinEndpointIds(componentId, pinName, endpointPinResolver) {
  if (!componentId || !pinName) {
    return [];
  }

  const componentPins =
    endpointPinResolver?.pinsByComponentId?.get(String(componentId)) || [];

  return uniqueValues(
    componentPins
      .filter((pin) => resolveArduinoUnoPinName(pin) === pinName)
      .map((pin) => String(pin?.marker_key || pin?.markerKey || '').trim())
      .filter(Boolean)
      .map((markerKey) => buildEndpointId(componentId, markerKey))
  );
}

function buildArduinoUnoDigitalInputsByPinName(primaryUnoComponentId, context) {
  if (!primaryUnoComponentId) {
    return {};
  }

  return Object.fromEntries(
    ARDUINO_UNO_PIN_ORDER.map((pinDefinition) => {
      const startEndpointIds = findArduinoUnoBoardPinEndpointIds(
        primaryUnoComponentId,
        pinDefinition.name,
        context.endpointPinResolver
      );
      const reachability = buildTerminalReachabilityFromEndpointIds(
        startEndpointIds.length > 0
          ? startEndpointIds
          : [buildEndpointId(primaryUnoComponentId, pinDefinition.name)],
        context
      );
      const externalPotentials = reachability.potentials.filter((potential) => (
        potential?.label !== pinDefinition.name
      ));
      const inputState = resolveDigitalInputStateFromPotentials(externalPotentials);

      return [
        pinDefinition.name,
        {
          pinName: pinDefinition.name,
          level: inputState.level,
          status: inputState.status,
          boardPins: reachability.boardPins,
          pinNames: reachability.pinNames,
          potentials: externalPotentials,
          rails: reachability.rails,
        },
      ];
    })
  );
}

function buildArduinoUnoAnalogInputsByPinName(
  primaryUnoComponentId,
  context,
  componentAnalyses = [],
  analogResistiveNetwork = null
) {
  if (!primaryUnoComponentId) {
    return {};
  }

  const analogSourceAnalyses = (Array.isArray(componentAnalyses) ? componentAnalyses : [])
    .filter((analysis) => analysis?.analysisType === 'analog_source');
  const reachabilityAdjacency = analogResistiveNetwork?.hardAdjacency || context.adjacency;

  return Object.fromEntries(
    ARDUINO_UNO_ANALOG_PIN_NAMES.map((pinName) => {
      const startEndpointIds = findArduinoUnoBoardPinEndpointIds(
        primaryUnoComponentId,
        pinName,
        context.endpointPinResolver
      );
      const resolvedStartEndpointIds = startEndpointIds.length > 0
        ? startEndpointIds
        : [buildEndpointId(primaryUnoComponentId, pinName)];
      const reachability = buildTerminalReachabilityFromEndpointIds(
        resolvedStartEndpointIds,
        context,
        reachabilityAdjacency
      );
      const externalPotentials = reachability.potentials.filter((potential) => (
        potential?.label !== pinName
      ));
      const fixedVoltageState = resolveAnalogVoltageStateFromPotentials(externalPotentials);
      const connectedAnalogSources = analogSourceAnalyses.filter((analysis) => (
        Array.isArray(analysis?.sourcePinNames) &&
        analysis.sourcePinNames.includes(pinName)
      ));
      const resistiveNetworkState = analogResistiveNetwork
        ? analyzeArduinoUnoAnalogPinViaResistiveNetwork(
            resolvedStartEndpointIds,
            analogResistiveNetwork
          )
        : null;
      const resolvedSourceVoltages = connectedAnalogSources
        .filter((analysis) => analysis?.status === 'resolved' && Number.isFinite(Number(analysis?.voltage)))
        .map((analysis) => Number(analysis.voltage));
      const hasSourceConflict = connectedAnalogSources.some((analysis) => analysis?.status === 'conflict');
      const combinedVoltages = [];

      if (fixedVoltageState.status === 'resolved') {
        combinedVoltages.push(Number(fixedVoltageState.voltage));
      }

      resolvedSourceVoltages.forEach((voltage) => {
        if (!combinedVoltages.some((knownVoltage) => isApproximatelyEqual(knownVoltage, voltage))) {
          combinedVoltages.push(voltage);
        }
      });

      let status = 'floating';
      let voltage = null;
      let sourceAnalyses = connectedAnalogSources;

      if (resistiveNetworkState?.status === 'resolved') {
        status = 'resolved';
        voltage = Number(resistiveNetworkState.voltage);
        sourceAnalyses = Array.isArray(resistiveNetworkState.sourceAnalyses) &&
          resistiveNetworkState.sourceAnalyses.length > 0
          ? resistiveNetworkState.sourceAnalyses
          : connectedAnalogSources;
      } else if (resistiveNetworkState?.status === 'conflict') {
        status = 'conflict';
        sourceAnalyses = Array.isArray(resistiveNetworkState.sourceAnalyses) &&
          resistiveNetworkState.sourceAnalyses.length > 0
          ? resistiveNetworkState.sourceAnalyses
          : connectedAnalogSources;
      } else if (fixedVoltageState.status === 'conflict' || hasSourceConflict) {
        status = 'conflict';
      } else if (combinedVoltages.length === 1) {
        status = 'resolved';
        voltage = combinedVoltages[0];
      } else if (combinedVoltages.length > 1) {
        status = 'conflict';
      }

      return [
        pinName,
        {
          adcValue: status === 'resolved' ? toAdcValue(voltage) : null,
          boardPins: reachability.boardPins,
          pinName,
          pinNames: reachability.pinNames,
          potentials: externalPotentials,
          rails: reachability.rails,
          sourceAnalyses,
          status,
          voltage,
        },
      ];
    })
  );
}

function classifyLedDriveMode({
  anodePinNames,
  cathodePinNames,
  anodeRails,
  cathodeRails,
} = {}) {
  const safeAnodePinNames = Array.isArray(anodePinNames) ? anodePinNames : [];
  const safeCathodePinNames = Array.isArray(cathodePinNames) ? cathodePinNames : [];
  const safeAnodeRails = Array.isArray(anodeRails) ? anodeRails : [];
  const safeCathodeRails = Array.isArray(cathodeRails) ? cathodeRails : [];

  if (
    safeAnodePinNames.length === 1 &&
    safeCathodePinNames.length === 0 &&
    safeCathodeRails.includes('GND')
  ) {
    return {
      mode: 'source_drive',
      preferredPinName: safeAnodePinNames[0],
      description: 'Valid output LED path: GPIO drives the anode and the cathode returns to GND.',
    };
  }

  if (
    safeCathodePinNames.length === 1 &&
    safeAnodePinNames.length === 0 &&
    safeAnodeRails.includes('5V')
  ) {
    return {
      mode: 'sink_drive',
      preferredPinName: safeCathodePinNames[0],
      description: 'Valid active-low LED path: 5V feeds the anode and the GPIO sinks current through the cathode.',
    };
  }

  if (
    safeCathodePinNames.length === 1 &&
    safeAnodePinNames.length === 0 &&
    safeAnodeRails.includes('GND')
  ) {
    return {
      mode: 'reverse_biased_grounded_anode',
      preferredPinName: '',
      description: 'Reverse-biased path: the anode is tied to GND while the cathode is on the GPIO, so the LED will not light.',
    };
  }

  if (
    safeAnodePinNames.length === 1 &&
    safeCathodePinNames.length === 0 &&
    safeCathodeRails.includes('5V')
  ) {
    return {
      mode: 'reverse_biased_powered_cathode',
      preferredPinName: '',
      description: 'Reverse-biased path: the cathode is tied to 5V while the anode is on the GPIO, so the LED will not light.',
    };
  }

  return {
    mode: 'unresolved',
    preferredPinName: '',
    description: 'The simulator could not resolve this LED into a simple Uno-driven path yet.',
  };
}

function buildLedSimulationAnalysis(component, context, terminalPinKeys = {}) {
  const anodePinKey = String(terminalPinKeys?.anodePinKey || 'A').trim() || 'A';
  const cathodePinKey = String(terminalPinKeys?.cathodePinKey || 'K').trim() || 'K';
  const anode = buildTerminalReachability(component.id, anodePinKey, context);
  const cathode = buildTerminalReachability(component.id, cathodePinKey, context);
  const ledDrive = classifyLedDriveMode({
    anodePinNames: anode.pinNames,
    cathodePinNames: cathode.pinNames,
    anodeRails: anode.rails,
    cathodeRails: cathode.rails,
  });

  return {
    analysisType: 'led',
    componentId: component.id,
    componentName: getComponentDisplayName(component),
    anodePinKey,
    cathodePinKey,
    isOn:
      hasOnlyHighPotential(anode.potentials) &&
      hasOnlyLowPotential(cathode.potentials),
    driveMode: ledDrive.mode,
    driveDescription: ledDrive.description,
    preferredPinName: ledDrive.preferredPinName,
    anodePotentials: anode.potentials,
    cathodePotentials: cathode.potentials,
    anodePinNames: anode.pinNames,
    cathodePinNames: cathode.pinNames,
    anodeRails: anode.rails,
    cathodeRails: cathode.rails,
    anodeBoardPins: anode.boardPins,
    cathodeBoardPins: cathode.boardPins,
  };
}

function evaluateConductionRuleWhen(when, component, simulationRuntimeState) {
  const normalizedWhen = String(when || 'always').trim().toLowerCase();
  const isPressed = simulationRuntimeState?.pressedButtonIds?.has(component?.id);

  switch (normalizedWhen) {
    case 'pressed':
      return Boolean(isPressed);
    case 'released':
      return !isPressed;
    case 'always':
    default:
      return true;
  }
}

function findSimulationModelTerminalPinKey(simulationModel, targetRole) {
  const normalizedRole = String(targetRole || '').trim().toLowerCase();
  const terminalRoles = simulationModel?.terminalRoles || {};

  return Object.entries(terminalRoles).find(([, role]) => (
    String(role || '').trim().toLowerCase() === normalizedRole
  ))?.[0] || '';
}

function buildSimulationModelConductionEdges(component, simulationModel, simulationRuntimeState) {
  return (Array.isArray(simulationModel?.conductionRules)
    ? simulationModel.conductionRules
    : []
  )
    .filter((rule) => evaluateConductionRuleWhen(rule?.when, component, simulationRuntimeState))
    .map((rule) => ({
      fromPinKey: String(rule?.fromPinKey || '').trim(),
      toPinKey: String(rule?.toPinKey || '').trim(),
    }))
    .filter((rule) => rule.fromPinKey && rule.toPinKey);
}

function buildSimulationModelLedAnalysis(component, context, simulationModel) {
  const anodePinKey = findSimulationModelTerminalPinKey(simulationModel, 'anode');
  const cathodePinKey = findSimulationModelTerminalPinKey(simulationModel, 'cathode');

  if (!anodePinKey || !cathodePinKey) {
    return null;
  }

  return buildLedSimulationAnalysis(component, context, {
    anodePinKey,
    cathodePinKey,
  });
}

const PASSIVE_SIMULATION_MODELS = {
  buzzer: {
    analyzeComponent: buildBuzzerSimulationAnalysis,
  },
  lcd_16x2: {
    analyzeComponent: buildLcd16x2SimulationAnalysis,
  },
  potentiometer: {
    analyzeComponent: buildVariableResistorAnalysis,
  },
  seven_segment_display: {
    analyzeComponent: buildSevenSegmentSimulationAnalysis,
  },
  servo: {
    analyzeComponent: buildServoSimulationAnalysis,
  },
  variable_resistor: {
    analyzeComponent: buildVariableResistorAnalysis,
  },
};

function getPassiveSimulationModel(component) {
  if (component?.type !== 'passive') {
    return null;
  }

  const simulationModel = resolvePassiveComponentRuntimeSimulationModel(
    component,
    component?.passiveDefinition || null
  );
  const registeredModel = PASSIVE_SIMULATION_MODELS[simulationModel?.modelKey] ||
    PASSIVE_SIMULATION_MODELS[component.passiveType] ||
    null;

  return {
    simulationModel,
    getConductionEdges: ({ component: passiveComponent, simulationRuntimeState }) => {
      const configuredEdges = buildSimulationModelConductionEdges(
        passiveComponent,
        simulationModel,
        simulationRuntimeState
      );

      if (configuredEdges.length > 0) {
        return configuredEdges;
      }

      return registeredModel?.getConductionEdges?.({
        component: passiveComponent,
        simulationRuntimeState,
      }) || [];
    },
    analyzeComponent: (passiveComponent, context) => {
      if (simulationModel?.behavior === 'diode_led') {
        return buildSimulationModelLedAnalysis(passiveComponent, context, simulationModel);
      }

      if (typeof registeredModel?.analyzeComponent === 'function') {
        return registeredModel.analyzeComponent(passiveComponent, context, simulationModel);
      }

      return null;
    },
  };
}

function shouldTreatPassiveEdgesAsHardConnectivity(model) {
  const simulationModel = model?.simulationModel;
  const behavior = String(simulationModel?.behavior || '').trim();
  const modelKey = String(simulationModel?.modelKey || '').trim();

  return behavior === 'conduction' && modelKey !== 'resistor';
}

function addPassiveModelEdges(
  adjacency,
  component,
  simulationRuntimeState,
  modelPredicate = null
) {
  const model = getPassiveSimulationModel(component);

  if (typeof modelPredicate === 'function' && !modelPredicate(model, component)) {
    return;
  }

  const edges = model?.getConductionEdges?.({
    component,
    simulationRuntimeState,
  });

  (Array.isArray(edges) ? edges : []).forEach((edge) => {
    const fromPinKey = String(edge?.fromPinKey || '').trim();
    const toPinKey = String(edge?.toPinKey || '').trim();

    if (!fromPinKey || !toPinKey) {
      return;
    }

    addUndirectedEdge(
      adjacency,
      buildEndpointId(component.id, fromPinKey),
      buildEndpointId(component.id, toPinKey)
    );
  });
}

function analyzeSimulatedComponent(component, context) {
  const model = getPassiveSimulationModel(component);

  if (typeof model?.analyzeComponent !== 'function') {
    return null;
  }

  return model.analyzeComponent(component, context);
}

function buildArduinoSimulationCircuitAnalysis({
  components,
  connections,
  suggestionPins,
  simulationPinStates,
  pressedButtonIds,
} = {}) {
  const safeComponents = Array.isArray(components) ? components : [];
  const componentsById = new Map(
    safeComponents.map((component) => [String(component.id), component])
  );
  const simulationRuntimeState = {
    pressedButtonIds: normalizePressedButtonIds(pressedButtonIds),
  };
  const unoBoards = safeComponents
    .filter((component) => component?.type === 'board' && isArduinoUnoBoard(component.board))
    .map((component) => ({
      componentId: component.id,
      componentName: getComponentDisplayName(component),
      boardName: component?.board?.name || component?.name || 'Arduino Uno',
    }));
  const unoComponentIds = new Set(
    unoBoards.map((board) => board.componentId)
  );

  if (unoComponentIds.size === 0) {
    return {
      unoBoards: [],
      analogInputsByPinName: {},
      buzzerStates: [],
      componentAnalyses: [],
      digitalInputsByPinName: {},
      lcdStates: [],
      ledPaths: [],
      primaryUnoComponentId: '',
      preferredExternalLedPinName: '',
      sevenSegmentStates: [],
      servoStates: [],
    };
  }

  const endpointPinResolver = buildEndpointPinResolver(suggestionPins);
  const adjacency = buildCircuitAdjacency(
    safeComponents,
    connections,
    suggestionPins,
    simulationRuntimeState
  );
  const analogResistiveNetwork = buildAnalogResistiveNetwork({
    components: safeComponents,
    connections,
    endpointPinResolver,
    simulationPinStates,
    simulationRuntimeState,
    suggestionPins,
    unoComponentIds,
  });
  const analysisContext = {
    adjacency,
    componentsById,
    endpointPinResolver,
    simulationPinStates,
    simulationRuntimeState,
    suggestionPins,
    unoBoards,
    unoComponentIds,
  };
  const componentAnalyses = [];
  const primaryUnoComponentId = String(unoBoards[0]?.componentId || '').trim();
  let preferredExternalLedPinName = '';

  safeComponents.forEach((component) => {
    const componentAnalysis = analyzeSimulatedComponent(component, analysisContext);

    if (!componentAnalysis) {
      return;
    }

    componentAnalyses.push(componentAnalysis);

    if (componentAnalysis.analysisType !== 'led') {
      return;
    }

    if (!preferredExternalLedPinName && componentAnalysis.preferredPinName) {
      preferredExternalLedPinName = componentAnalysis.preferredPinName;
    }
  });

  const digitalInputsByPinName = buildArduinoUnoDigitalInputsByPinName(
    primaryUnoComponentId,
    analysisContext
  );
  const analogInputsByPinName = buildArduinoUnoAnalogInputsByPinName(
    primaryUnoComponentId,
    analysisContext,
    componentAnalyses,
    analogResistiveNetwork
  );

  return {
    unoBoards,
    analogInputsByPinName,
    buzzerStates: componentAnalyses.filter((analysis) => analysis.analysisType === 'buzzer'),
    componentAnalyses,
    digitalInputsByPinName,
    lcdStates: componentAnalyses.filter((analysis) => analysis.analysisType === 'lcd_16x2'),
    ledPaths: componentAnalyses.filter((analysis) => analysis.analysisType === 'led'),
    primaryUnoComponentId,
    preferredExternalLedPinName,
    sevenSegmentStates: componentAnalyses.filter((analysis) => analysis.analysisType === 'seven_segment_display'),
    servoStates: componentAnalyses.filter((analysis) => analysis.analysisType === 'servo'),
  };
}

export function deriveSimulationLedStatesByComponentId({
  components,
  connections,
  suggestionPins,
  simulationPinStates,
  isSimulationActive,
  pressedButtonIds,
} = {}) {
  if (!isSimulationActive) {
    return {};
  }

  const analysis = buildArduinoSimulationCircuitAnalysis({
    components,
    connections,
    suggestionPins,
    simulationPinStates,
    pressedButtonIds,
  });

  return Object.fromEntries(
    analysis.ledPaths.map((ledPath) => [
      ledPath.componentId,
      {
        isOn: ledPath.isOn,
        anodePotentials: ledPath.anodePotentials,
        cathodePotentials: ledPath.cathodePotentials,
      },
    ])
  );
}

export function findPreferredArduinoUnoExternalLedPinName({
  components,
  connections,
  suggestionPins,
  pressedButtonIds,
} = {}) {
  return buildArduinoSimulationCircuitAnalysis({
    components,
    connections,
    suggestionPins,
    pressedButtonIds,
  }).preferredExternalLedPinName;
}

export function inspectArduinoSimulationCircuit({
  components,
  connections,
  suggestionPins,
  simulationPinStates,
  pressedButtonIds,
} = {}) {
  return buildArduinoSimulationCircuitAnalysis({
    components,
    connections,
    suggestionPins,
    simulationPinStates,
    pressedButtonIds,
  });
}
